// services/api.js
const API_BASE_URL = 'https://easemyhomeservices-backend.vercel.app/api';

// Token management
const getToken = () => localStorage.getItem('jwtToken');
const setToken = (token) => localStorage.setItem('jwtToken', token);
const clearToken = () => localStorage.removeItem('jwtToken');
const setUser = (user) => localStorage.setItem('user', JSON.stringify(user));
const clearUser = () => localStorage.removeItem('user');

// Get auth headers
const getAuthHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

// API request wrapper
const apiRequest = async (endpoint, options = {}, requiresAuth = true) => {
  try {
    const headers = requiresAuth ? getAuthHeaders() : { 'Content-Type': 'application/json' };
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      if (response.status === 401 && requiresAuth) {
        // Token expired or invalid
        clearToken();
        clearUser();
        window.location.href = '/';
        throw new Error('Session expired. Please login again.');
      }
      throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    return data;
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
};

// ==================== AUTH ENDPOINTS ====================

export const registerUser = async (email, password, name, phone) => {
  try {
    const data = await apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, phone }),
    }, false);
    
    if (data.success) {
      setToken(data.jwtToken);
      setUser(data.user);
      return {
        success: true,
        user: data.user,
        jwtToken: data.jwtToken,
      };
    }
    
    throw new Error(data.message || 'Registration failed');
  } catch (error) {
    console.error('Register error:', error);
    throw error;
  }
};

export const loginUser = async (email, password) => {
  try {
    const data = await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }, false);
    
    if (data.success) {
      setToken(data.jwtToken);
      setUser(data.user);
      return {
        success: true,
        user: data.user,
        jwtToken: data.jwtToken,
      };
    }
    
    throw new Error(data.message || 'Login failed');
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const validateToken = async () => {
  try {
    const token = getToken();
    if (!token) {
      return { success: false, message: 'No token found' };
    }
    
    const data = await apiRequest('/auth/validate', {
      method: 'POST',
    });
    
    return data;
  } catch (error) {
    console.error('Token validation error:', error);
    throw error;
  }
};

export const logoutUser = async () => {
  try {
    await apiRequest('/auth/logout', {
      method: 'POST',
    });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    clearToken();
    clearUser();
  }
};

// ==================== USER ENDPOINTS ====================

export const getUserProfile = async () => {
  try {
    const data = await apiRequest('/users/profile', {
      method: 'GET',
    });
    
    return data;
  } catch (error) {
    console.error('Get profile error:', error);
    throw error;
  }
};

export const updateUserProfile = async (profileData) => {
  try {
    const data = await apiRequest('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
    
    return data;
  } catch (error) {
    console.error('Update profile error:', error);
    throw error;
  }
};

export const updateUserAddress = async (addressData) => {
  try {
    const data = await apiRequest('/users/address', {
      method: 'PUT',
      body: JSON.stringify({
        fullName: addressData.fullName,
        phone: addressData.phone,
        address: addressData.address,
        city: addressData.city,
        state: addressData.state,
        pincode: addressData.pincode,
        landmark: addressData.landmark,
      }),
    });
    
    return data;
  } catch (error) {
    console.error('Update address error:', error);
    throw error;
  }
};

// ==================== BOOKING ENDPOINTS ====================

export const createBooking = async (bookingData) => {
  try {
    const data = await apiRequest('/bookings', {
      method: 'POST',
      body: JSON.stringify({
        service: bookingData.service,
        serviceType: bookingData.serviceType,
        date: bookingData.date,
        timeSlot: bookingData.timeSlot,
        address: {
          street: bookingData.address.address,
          city: bookingData.address.city,
          state: bookingData.address.state,
          pincode: bookingData.address.pincode,
          landmark: bookingData.address.landmark,
        },
        servicePrice: bookingData.servicePrice,
        notes: bookingData.additionalNotes,
      }),
    });
    
    return {
      id: data.booking.id,
      ...data.booking,
      otp: data.otp,
    };
  } catch (error) {
    console.error('Create booking error:', error);
    throw error;
  }
};

export const getMyBookings = async () => {
  try {
    const data = await apiRequest('/bookings', {
      method: 'GET',
    });
    
    return data.bookings;
  } catch (error) {
    console.error('Get bookings error:', error);
    throw error;
  }
};

export const getBookingById = async (bookingId) => {
  try {
    const data = await apiRequest(`/bookings/${bookingId}`, {
      method: 'GET',
    });
    
    return data.booking;
  } catch (error) {
    console.error('Get booking error:', error);
    throw error;
  }
};

export const verifyBookingOTP = async (bookingId, otp) => {
  try {
    const data = await apiRequest(`/bookings/${bookingId}/verify-otp`, {
      method: 'POST',
      body: JSON.stringify({ otp }),
    });
    
    return data;
  } catch (error) {
    console.error('Verify OTP error:', error);
    throw error;
  }
};

export const completeService = async (bookingId) => {
  try {
    const data = await apiRequest(`/bookings/${bookingId}/complete`, {
      method: 'PUT',
    });
    
    return data;
  } catch (error) {
    console.error('Complete service error:', error);
    throw error;
  }
};

export const rateBooking = async (bookingId, rating, review = '') => {
  try {
    const data = await apiRequest(`/bookings/${bookingId}/rate`, {
      method: 'PUT',
      body: JSON.stringify({ rating, review }),
    });
    
    return data;
  } catch (error) {
    console.error('Rate booking error:', error);
    throw error;
  }
};

export const cancelBooking = async (bookingId) => {
  try {
    const data = await apiRequest(`/bookings/${bookingId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'cancelled' }),
    });
    
    return data;
  } catch (error) {
    console.error('Cancel booking error:', error);
    throw error;
  }
};

export const getBookingOTP = async (bookingId) => {
  try {
    const data = await apiRequest(`/bookings/${bookingId}/otp`, {
      method: 'GET',
    });
    
    return data;
  } catch (error) {
    console.error('Get booking OTP error:', error);
    throw error;
  }
};

export const getBookingStats = async () => {
  try {
    const data = await apiRequest('/bookings/stats', {
      method: 'GET',
    });
    
    return data.stats;
  } catch (error) {
    console.error('Get booking stats error:', error);
    throw error;
  }
};

// ==================== SERVICES ENDPOINTS ====================

export const getServices = async (category = '', search = '', bhk = '1BHK', popular = false) => {
  try {
    let url = '/services';
    const params = new URLSearchParams();
    
    if (category && category !== 'All Services') params.append('category', category);
    if (search) params.append('search', search);
    if (bhk) params.append('bhk', bhk);
    if (popular) params.append('popular', 'true');
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const data = await apiRequest(url, {
      method: 'GET',
    }, false);
    
    return data.services;
  } catch (error) {
    console.error('Get services error:', error);
    throw error;
  }
};

export const getServiceById = async (serviceId, bhk = '1BHK', carpetArea = null) => {
  try {
    const params = new URLSearchParams();
    if (bhk) params.append('bhk', bhk);
    if (carpetArea) params.append('carpetArea', carpetArea);
    
    const url = `/services/${serviceId}${params.toString() ? '?' + params.toString() : ''}`;
    const data = await apiRequest(url, {
      method: 'GET',
    }, false);
    
    return data.service;
  } catch (error) {
    console.error('Get service by ID error:', error);
    throw error;
  }
};

export const getServiceByName = async (serviceName, bhk = '1BHK') => {
  try {
    // Convert service name to service ID format (lowercase with hyphens)
    const serviceId = serviceName.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-');
    return await getServiceById(serviceId, bhk);
  } catch (error) {
    console.error('Get service by name error:', error);
    throw error;
  }
};

export const getServiceCategories = async () => {
  try {
    const data = await apiRequest('/services/categories/list', {
      method: 'GET',
    }, false);
    
    return data.categories;
  } catch (error) {
    console.error('Get service categories error:', error);
    throw error;
  }
};

export const getServicesByCategory = async (categoryName, bhk = '1BHK') => {
  try {
    const encodedName = encodeURIComponent(categoryName);
    const params = new URLSearchParams();
    if (bhk) params.append('bhk', bhk);
    
    const url = `/services/category/${encodedName}${params.toString() ? '?' + params.toString() : ''}`;
    const data = await apiRequest(url, {
      method: 'GET',
    }, false);
    
    return data.services;
  } catch (error) {
    console.error('Get services by category error:', error);
    throw error;
  }
};

export const getPopularServices = async (limit = 10, bhk = '1BHK') => {
  try {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    if (bhk) params.append('bhk', bhk);
    
    const url = `/services/popular/list${params.toString() ? '?' + params.toString() : ''}`;
    const data = await apiRequest(url, {
      method: 'GET',
    }, false);
    
    return data.services;
  } catch (error) {
    console.error('Get popular services error:', error);
    throw error;
  }
};

export const getServicePricing = async (serviceId) => {
  try {
    const data = await apiRequest(`/services/pricing/${serviceId}`, {
      method: 'GET',
    }, false);
    
    return data.pricing;
  } catch (error) {
    console.error('Get service pricing error:', error);
    throw error;
  }
};

export const calculateServicePrice = async (serviceId, bhk = '1BHK', carpetArea = null) => {
  try {
    const requestBody = { serviceId };
    if (bhk) requestBody.bhk = bhk;
    if (carpetArea) requestBody.carpetArea = carpetArea;
    
    const data = await apiRequest('/services/calculate-price', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    }, false);
    
    return data;
  } catch (error) {
    console.error('Calculate service price error:', error);
    throw error;
  }
};

export const getSubCategories = async (categoryName) => {
  try {
    const encodedName = encodeURIComponent(categoryName);
    const data = await apiRequest(`/services/subcategories/${encodedName}`, {
      method: 'GET',
    }, false);
    
    return data.subCategories;
  } catch (error) {
    console.error('Get sub-categories error:', error);
    throw error;
  }
};

// ==================== HEALTH ENDPOINTS ====================

export const healthCheck = async () => {
  try {
    const response = await fetch(`${API_BASE_URL.replace('/api', '')}/health`);
    return await response.json();
  } catch (error) {
    console.error('Health check error:', error);
    throw error;
  }
};

export const getRootInfo = async () => {
  try {
    const response = await fetch(`${API_BASE_URL.replace('/api', '')}/`);
    return await response.json();
  } catch (error) {
    console.error('Get root info error:', error);
    throw error;
  }
};

// ==================== HELPER FUNCTIONS ====================

export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error parsing user:', error);
    return null;
  }
};

export const isAuthenticated = () => {
  const token = getToken();
  return !!token;
};

export const getJwtToken = () => getToken();